import { Captcha } from "components/Captcha";
import { LoadingSpinner } from "components/LoadingSpinner";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { InputText } from "primereact/inputtext";
import { Message } from "primereact/message";
import { useCallback, useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
// Required whenever renderTextLayer is on: without it react-pdf v9 positions the text spans
// wrongly and they render as visible garbage over the page instead of an invisible select layer.
import "react-pdf/dist/esm/Page/TextLayer.css";
import { useParams } from "react-router-dom";
import {
  complianceFormPdfDownloadUrl,
  complianceFormPdfUrl,
  useGetPublicComplianceFormQuery,
  useSubmitComplianceAttestationMutation,
} from "store/queries/finixCompliance";
import styled from "styled-components";

/**
 * CLE-2718 — public PCI SAQ A attestation page.
 *
 * Reached only through an opaque token that a super admin copies out of the account's Finix panel
 * and emails by hand. The person on the other end is a hospital executive with no ClearHealth
 * account, so there is no login, and nothing on this page ever names a Finix form or merchant.
 *
 * The input surface is deliberately tiny — name, title, accept — because it is the whole of Part 3b
 * of the document. Every other field on the 16 pages comes from an application-level template that
 * neither we nor the hospital can edit, so there is no field-editing UI to build.
 */

// The SPA's CSP sets `object-src 'none'`, so <embed>/<object> are dead ends. It does allow
// `worker-src 'self' blob:`, which is what pdf.js needs. Same worker wiring as MobileReceiptViewer.
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url
).toString();

const Wrapper = styled.div`
  width: 100%;
  max-width: 900px;
  padding: 0 16px 48px;
`;

const DocumentFrame = styled.div`
  background-color: #f9f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 12px;
  max-height: 70vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`;

const PageWrapper = styled.div`
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.15);
`;

const Notice = styled.p`
  color: #6c757d;
  font-size: 13px;
  margin: 8px 0 0;
`;

function formatDate(value: string | null | undefined): string {
  if (!value) return "";
  return new Date(value).toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export function ComplianceAttestation() {
  const { token = "" } = useParams<{ token: string }>();
  const {
    data: form,
    isLoading,
    isError,
    error,
  } = useGetPublicComplianceFormQuery(token, { skip: !token });
  const [submitAttestation, { isLoading: isSubmitting }] =
    useSubmitComplianceAttestationMutation();

  const containerRef = useRef<HTMLDivElement>(null);
  const [pageWidth, setPageWidth] = useState<number>();
  const [numPages, setNumPages] = useState(0);

  const [name, setName] = useState("");
  const [title, setTitle] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  // Pre-fill from the Finix contact once, without pinning the fields: if Finix holds the wrong
  // person — and in production 8 of the 14 forms name the same CEO at a personal address — the
  // attester types over it. That is the entire reason name and title are free text.
  useEffect(() => {
    if (!form) return;
    setName((current) => current || form.prefillName || "");
    setTitle((current) => current || form.prefillTitle || "");
  }, [form]);

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current)
        setPageWidth(containerRef.current.clientWidth - 24);
    };
    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  const onDocumentLoadSuccess = useCallback(
    ({ numPages: loadedPages }: { numPages: number }) =>
      setNumPages(loadedPages),
    []
  );

  const handleSubmit = async () => {
    setSubmitError(null);
    try {
      await submitAttestation({
        token,
        name: name.trim(),
        title: title.trim(),
        captchaToken,
      }).unwrap();
      setSubmitted(true);
    } catch (err: any) {
      setSubmitError(
        err?.data?.message ||
          err?.data?.messages?.[0] ||
          "We could not submit your attestation. Please try again."
      );
    }
  };

  if (!token || isError) {
    // One message for every token failure — unknown, rotated, expired, malformed. The API answers
    // an identical 404 for all four and never says which, so neither does this.
    const message =
      (error as any)?.data?.message ||
      "This link is no longer valid. Please ask your ClearHealth contact for a new one.";
    return (
      <Wrapper>
        <Message severity="error" text={message} className="w-full" />
      </Wrapper>
    );
  }

  if (isLoading || !form) {
    return (
      <Wrapper>
        <LoadingSpinner />
      </Wrapper>
    );
  }

  const alreadyAttested = submitted || form.isAccepted;
  const canSubmit =
    form.canAttest &&
    !alreadyAttested &&
    accepted &&
    !!captchaToken &&
    name.trim().length >= 2 &&
    title.trim().length >= 2 &&
    !isSubmitting;

  return (
    <Wrapper>
      <h2 className="mt-0">PCI Attestation of Compliance</h2>
      <p className="text-color-secondary">
        Please review the document below and confirm your name and title to
        complete your annual PCI SAQ A attestation.
        {form.dueAt
          ? ` This attestation is due ${formatDate(form.dueAt)}.`
          : ""}
      </p>

      {form.state === "OVERDUE" && !alreadyAttested && (
        <Message
          severity="warn"
          text="This attestation is past its due date. Completing it now brings the merchant back into compliance."
          className="w-full mb-3"
        />
      )}

      {form.state === "INVALID" && (
        // Surfaced rather than hidden: an INVALID form is not "done", and the merchant behind it
        // has no valid form at all until Finix issues a replacement.
        <Message
          severity="error"
          text="This attestation form is no longer valid. Please contact your ClearHealth representative for a replacement."
          className="w-full mb-3"
        />
      )}

      <div className="flex justify-content-end mb-2">
        {/* Hits the same proxy with ?download=1, which flips Content-Disposition to attachment so
            this is a real file save rather than a hand-off to the browser's PDF viewer. Opened in a
            new tab so a blocked download never navigates the signer away from the form. */}
        <a
          href={complianceFormPdfDownloadUrl(token)}
          target="_blank"
          rel="noopener noreferrer"
          className="p-button p-button-text p-button-sm no-underline"
        >
          <i className="pi pi-download mr-2" aria-hidden />
          Download PDF
        </a>
      </div>

      <DocumentFrame ref={containerRef}>
        <Document
          file={complianceFormPdfUrl(token)}
          loading={<LoadingSpinner />}
          error={
            <Notice>
              Unable to display the document. Please contact your ClearHealth
              representative.
            </Notice>
          }
          onLoadSuccess={onDocumentLoadSuccess}
        >
          {Array.from({ length: numPages }, (_, index) => (
            <PageWrapper key={`compliance-page-${index + 1}`}>
              {/* The text layer is ON deliberately, unlike MobileReceiptViewer (whose default
                  this component otherwise follows). This is a 16-page legal document the signer is
                  expected to READ before attesting: without a text layer it cannot be searched
                  with Cmd+F, cannot be selected or copied, and is invisible to a screen reader.
                  The performance cost is worth paying for a document someone signs. */}
              <Page
                pageNumber={index + 1}
                width={pageWidth}
                renderTextLayer
                renderAnnotationLayer={false}
              />
            </PageWrapper>
          ))}
        </Document>
      </DocumentFrame>

      {alreadyAttested ? (
        <Message
          severity="success"
          text={
            form.signedAt || submitted
              ? "Thank you. This attestation has been signed and submitted to Finix."
              : "This attestation has already been completed."
          }
          className="w-full mt-4"
        />
      ) : (
        <div className="mt-4">
          <div className="grid">
            <div className="col-12 md:col-6 flex flex-column gap-2">
              <label htmlFor="attestation-name">Name</label>
              <InputText
                id="attestation-name"
                value={name}
                maxLength={200}
                onChange={(e) => setName(e.target.value)}
                disabled={!form.canAttest || isSubmitting}
              />
            </div>
            <div className="col-12 md:col-6 flex flex-column gap-2">
              <label htmlFor="attestation-title">Title</label>
              <InputText
                id="attestation-title"
                value={title}
                maxLength={200}
                onChange={(e) => setTitle(e.target.value)}
                disabled={!form.canAttest || isSubmitting}
              />
            </div>
          </div>

          <div className="flex align-items-start gap-2 mt-3">
            <Checkbox
              inputId="attestation-accept"
              checked={accepted}
              onChange={(e) => setAccepted(!!e.checked)}
              disabled={!form.canAttest || isSubmitting}
            />
            <label htmlFor="attestation-accept">
              I confirm that I am authorized to sign this Attestation of
              Compliance on behalf of the merchant, and that the information
              above is accurate.
            </label>
          </div>

          {/* Same posture as the Direct Pay public link. Self-bypasses on localhost, so local
              testing is unaffected. It cannot stop a human who holds the link from signing —
              nothing can — but it blocks automated submission against a forwarded URL. */}
          <div className="mt-3">
            <Captcha
              onVerify={(value) => setCaptchaToken(value)}
              onExpired={() => setCaptchaToken("")}
              onError={() => setCaptchaToken("")}
            />
          </div>

          {submitError && (
            <Message
              severity="error"
              text={submitError}
              className="w-full mt-3"
            />
          )}

          <Button
            label={isSubmitting ? "Submitting…" : "Submit Attestation"}
            className="mt-3"
            disabled={!canSubmit}
            loading={isSubmitting}
            onClick={handleSubmit}
          />

          <Notice>
            Submitting records your name, title, the date, and your IP address
            as the electronic signature on this attestation. This cannot be
            undone.
          </Notice>
        </div>
      )}
    </Wrapper>
  );
}
